import { useEffect, useState } from 'preact/hooks';
import Router from 'preact-router';
import { lazy, Suspense } from 'preact/compat';
import { isAuthenticated, showAuthPrompt } from './core/auth/store';
import { AuthPrompt } from './core/auth/AuthPrompt';
import { TabBar } from './shell/TabBar';
import { BannerSlot } from './shell/BannerSlot';
import { Toast, ToastProvider } from './shell/Toast';
import { FAB } from './shell/FAB';
import { NoticeSheet } from './shell/NoticeSheet';
import { syncTextScale } from './core/preferences/text-scale';

// Lazy-loaded modules - only downloaded when their route is active
const ChatModule = lazy(() => import('./modules/chat'));
const ApprovalsModule = lazy(() => import('./modules/approvals'));
const DealsModule = lazy(() => import('./modules/deals'));
const PeopleModule = lazy(() => import('./modules/people'));
const TodayModule = lazy(() => import('./modules/today'));
const FinanceModule = lazy(() => import('./modules/finance'));
const TradingModule = lazy(() => import('./modules/trading'));
const WatchesModule = lazy(() => import('./modules/watches'));

function LoadingSpinner() {
  return (
    <div class="loading-spinner-container">
      <div class="loading-spinner" />
    </div>
  );
}

export function App() {
  // Guard once, before the router reads a query whose percent escapes are broken.
  const [malformedNotice] = useState(() => {
    try { decodeURIComponent(window.location.search); } catch {
      const hadNotice = new URLSearchParams(window.location.search).has('notice');
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.hash);
      return hadNotice;
    }
    return false;
  });
  useEffect(() => {
    if (!isAuthenticated.value) return;
    void syncTextScale().catch(() => {
      // Keep the local preference when the server is temporarily unavailable.
    });
  }, [isAuthenticated.value]);

  if (showAuthPrompt.value || !isAuthenticated.value) {
    return <AuthPrompt />;
  }

  // No app-wide lock: the PIN/fingerprint gate is scoped to Reflections chats only
  // (rendered by ChatView when a private chat is opened).
  return (
    <ToastProvider>
      <div class="app">
        <BannerSlot />
        <main class="app-content">
          <Suspense fallback={<LoadingSpinner />}>
            <Router>
              <ChatModule path="/" />
              <ChatModule path="/chat" />
              <ApprovalsModule path="/approvals" />
              <ApprovalsModule path="/approvals/:id" />
              <DealsModule path="/deals" />
              <DealsModule path="/deals/:id" />
              <PeopleModule path="/people/:id" />
              <TodayModule path="/today" />
              <TodayModule path="/today/open" />
              <TodayModule path="/today/autopilot" />
              <FinanceModule path="/finance" />
              <TradingModule path="/trading" />
              <WatchesModule path="/watches" />
            </Router>
          </Suspense>
        </main>
        <FAB />
        <TabBar />
        <Toast />
        <NoticeSheet missingOnLaunch={malformedNotice} />
      </div>
    </ToastProvider>
  );
}
